/**
 * Client-side error reporting.
 *
 * Why this file exists
 * --------------------
 * `MainLayout.componentDidCatch` used to be `console.log(...)` with its recovery
 * path commented out. When a render error unmounts the tree (the checkout funnel
 * is the expensive case) nobody outside the customer's own browser ever learned
 * about it. Root `CLAUDE.md` standard 5 forbids "console as the only handling".
 *
 * Which sink, and why this one
 * ----------------------------
 * There is no error-reporting infrastructure in this estate — no Sentry, no log
 * pipeline reachable from the browser, and adding a dependency is out of scope
 * for item 21c. The only *existing* sink that durably records a browser-side
 * error is `POST /socket_error`, which bt-backend persists to the `Chat_Debug`
 * table (`bt-backend/controllers/chat.js:569-577`, route
 * `bt-backend/routes/chat.routes.js:237`, anonymous-auth so it works for
 * logged-out visitors). It is a debug table, not an alerting system: nothing
 * pages anyone when a row lands. So this module deliberately does **not** claim
 * to "convert a silent outage into an alert" — it converts it into a durable,
 * queryable record plus a machine-assertable in-page trace. Wiring an actual
 * alert on `Chat_Debug` is a follow-up item (see
 * `work/21c-frontend-critical-path/design.md`, N-3 resolution).
 *
 * Because the sink is unwatched, the error must also stay *visible*:
 *   - `console.error` keeps it in the browser console, so the existing
 *     `errors-in-console` Lighthouse audit and a `claude-in-chrome` run still
 *     see it;
 *   - every report is appended to `window.__BT_CLIENT_ERRORS__`, which gives an
 *     automated check something exact to assert on (test T13) and records
 *     whether delivery to the sink actually succeeded.
 * Nothing here swallows: a failed POST is logged *and* marked `delivered: false`
 * on the retained record.
 *
 * PII: root `CLAUDE.md` rule 4. The report carries no headers, no cookies, no
 * token, no user id, and `location.search`/`location.hash` are dropped because
 * this app puts coupon codes and `eid` in the query string.
 */

/** Global key holding the in-page trace of reported errors. Asserted by test T13. */
export const CLIENT_ERROR_RECORD_KEY = '__BT_CLIENT_ERRORS__';

/** Truncation limits. `Chat_Debug.log_error` is a shared debug table — stay small. */
const MAX_MESSAGE_CHARS = 500;
const MAX_STACK_CHARS = 2000;
const MAX_COMPONENT_STACK_CHARS = 2000;

/**
 * Truncate a value to a character budget, marking it when it was cut.
 *
 * @param {string|null|undefined} value - Raw text.
 * @param {number} max - Maximum characters to keep.
 * @returns {string|null} Truncated text, or `null` when there was nothing to keep.
 */
function truncate(value, max) {
    if (typeof value !== 'string' || value.length === 0) return null;
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…[truncated ${value.length - max} chars]`;
}

/**
 * Build the PII-free payload that is sent to the sink and retained in-page.
 *
 * Exported separately from the thunk so it can be unit-tested without Redux,
 * Express or a network (root `CLAUDE.md` standard 2).
 *
 * @param {object} params
 * @param {Error|*} params.error - The thrown value. Non-Error values are stringified.
 * @param {string|null} [params.componentStack] - React's `info.componentStack`; component names only.
 * @param {string} params.context - Where the report originated, e.g. `MainLayout.componentDidCatch`.
 * @returns {{context: string, name: string, message: string|null, stack: string|null,
 *            componentStack: string|null, path: string|null, at: string}}
 *          A plain, JSON-serialisable report containing no PII.
 */
export function buildErrorReport({ error, componentStack = null, context }) {
    const isError = error instanceof Error;

    return {
        context,
        name: isError ? error.name : typeof error,
        message: truncate(isError ? error.message : String(error), MAX_MESSAGE_CHARS),
        stack: truncate(isError ? error.stack : null, MAX_STACK_CHARS),
        componentStack: truncate(componentStack, MAX_COMPONENT_STACK_CHARS),
        // Path only. `location.search` carries coupon codes and `eid` (MainLayout
        // reads `searchQuery.eid`), and `location.hash` carries OAuth fragments.
        path: typeof window !== 'undefined' && window.location ? window.location.pathname : null,
        at: new Date().toISOString(),
    };
}

/**
 * Append a report to the in-page trace so an automated check can assert on it.
 *
 * @param {object} params
 * @param {object} params.report - A report from {@link buildErrorReport}.
 * @returns {object|null} The retained record (the same object identity, so the
 *          caller can flip `delivered` on it), or `null` when there is no window
 *          (server-side render — `componentDidCatch` does not run there anyway).
 */
function retainErrorReport({ report }) {
    if (typeof window === 'undefined') return null;
    if (!Array.isArray(window[CLIENT_ERROR_RECORD_KEY])) {
        window[CLIENT_ERROR_RECORD_KEY] = [];
    }
    const record = { ...report, delivered: false };
    window[CLIENT_ERROR_RECORD_KEY].push(record);
    return record;
}

/**
 * Report a client-side error: keep it visible in the console, retain it in-page,
 * and persist it to the only durable sink this estate has.
 *
 * Never rejects — callers (error boundaries) must not have to handle a failure
 * of the failure reporter, and an unterminated promise would violate root
 * `CLAUDE.md` standard 1.
 *
 * @param {object} params
 * @param {Error|*} params.error - The thrown value.
 * @param {string|null} [params.componentStack] - React's `info.componentStack`.
 * @param {string} params.context - Origin label, e.g. `MainLayout.componentDidCatch`.
 * @returns {function(Function, Function, {axNL: object}): Promise<void>} Redux thunk.
 * @throws Nothing. Delivery failures are logged and recorded, not thrown.
 */
export function reportClientError({ error, componentStack = null, context }) {
    return async (dispatch, getState, { axNL }) => {
        const report = buildErrorReport({ error, componentStack, context });

        // Re-surface first: the sink is unwatched, the console is not. Doing this
        // before the await means the error is visible even if the POST hangs.
        console.error('[bt] client error', report);

        const record = retainErrorReport({ report });

        try {
            // `axNL` is the no-loading-spinner client — a background report must
            // not put the whole app into its loading state.
            await axNL.post('/socket_error', { errLog: JSON.stringify(report) });
            if (record) record.delivered = true;
        } catch (deliveryError) {
            // Not swallowed: the failure to deliver is itself surfaced and recorded.
            // `record.delivered` stays false so T13 can tell "reported but the sink
            // was unreachable" from "never reported at all".
            console.error(
                '[bt] client error report could not be delivered to /socket_error:',
                deliveryError && deliveryError.message
            );
        }
    };
}
